import Icons from "@/assets";
import { isAxiosError } from "axios";
import { MediaSection, ApiMedia, MediaItem, BlurResult } from "@/types/common";
export { getAccessibilityLabel } from "@/shared/constants/accessibility";

export const getApiErrorMessage = (err: unknown, fallback: string): string => {
  if (!isAxiosError(err)) return fallback;

  if (err.response?.status === 413) {
    const backendMessage = (err.response.data as { message?: string })?.message;
    if (backendMessage) {
      return backendMessage; 
    }
    return "Total upload size exceeds server limits. Please compress or select smaller files.";
  }

  const responseData = err.response?.data as
    | { message?: string; data?: unknown }
    | undefined;
  if (typeof responseData?.data === "string" && responseData.data) {
    return responseData.data;
  }
  return responseData?.message || fallback;
};

type NoteItem = {
  notes_data_id: string;
  notes: string;
  other_notes?: string | null;
};

type TransformedNote = {
  default_note_id: string;
  note: string;
};

export const orderPhotosByView = <T extends { url: string }>(
  photos: T[],
  photoViews: string[],
): (T | null)[] => {
  const ordered: (T | null)[] = new Array(photoViews.length).fill(
    null,
  );

  photos.forEach((photo) => {
    const lowerName = photo.url.toLowerCase();
    const view = photoViews.find((v) => lowerName.includes(v.toLowerCase()));

    if (view) {
      const idx = photoViews.indexOf(view);
      // Only place it there if that slot is empty or we are specifically matching this view
      if (ordered[idx] === null) {
        ordered[idx] = photo;
      } else {
        // If slot is taken, find first empty slot as fallback
        const firstEmptyIdx = ordered.findIndex((slot) => slot === null);
        if (firstEmptyIdx !== -1) ordered[firstEmptyIdx] = photo;
      }
    } else {
      // fallback: place in the first empty slot
      const firstEmptyIdx = ordered.findIndex((slot) => slot === null);
      if (firstEmptyIdx !== -1) ordered[firstEmptyIdx] = photo;
    }
  });

  return ordered;
};

export const transformNotes = (
  selection: Record<string, NoteItem[]>,
): TransformedNote[] =>
  Object.values(selection).flatMap((section) =>
    section
      .filter(
        (item) =>
          !(
            item.notes === "Other - type your own observation" &&
            (!item.other_notes || item.other_notes.trim() === "")
          ),
      )
      .map((item) => ({
        default_note_id: item.notes_data_id,
        note: item.other_notes || item.notes,
      })),
  );


export const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error("Failed to read file"));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};

export const checkAndResizeImage = (dataUrl: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const maxDimension = 800; // Maximum width or height

      if (img.width <= maxDimension && img.height <= maxDimension) {
        // No resizing needed
        resolve(dataUrl);
        return;
      }

      // Calculate new dimensions while maintaining aspect ratio
      let newWidth = img.width;
      let newHeight = img.height;

      if (img.width > img.height) {
        newWidth = maxDimension;
        newHeight = (img.height * maxDimension) / img.width;
      } else {
        newHeight = maxDimension;
        newWidth = (img.width * maxDimension) / img.height;
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;
      canvas.width = newWidth;
      canvas.height = newHeight;

      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      const resizedDataUrl = canvas.toDataURL("image/jpeg", 0.8);
      resolve(resizedDataUrl);
    };
    img.src = dataUrl;
  });
};

export const isFromGalleryCamera = async (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const buffer = new Uint8Array(e.target!.result as ArrayBuffer);

      // JPEG EXIF header check: 0xFF 0xD8 ... "Exif"
      let hasExif = false;
      for (let i = 0; i < buffer.length - 4; i++) {
        if (
          buffer[i] === 0x45 && // E
          buffer[i + 1] === 0x78 && // x
          buffer[i + 2] === 0x69 && // i
          buffer[i + 3] === 0x66 // f
        ) {
          hasExif = true;
          break;
        }
      }

      resolve(!hasExif); // No EXIF → Gallery Camera Capture
    };

    reader.readAsArrayBuffer(file);
  });
};

type ColorSet = {
  bg: string;
  border: string;
  text: string;
};

export const stringToColorSet = (name: string): ColorSet => {
  const colorSets: ColorSet[] = [
    { bg: "#E3F2FD", border: "#42A5F5", text: "#1E88E5" }, // Light Blue
    { bg: "#FCE4EC", border: "#EC407A", text: "#D81B60" }, // Pink
    { bg: "#E8F5E9", border: "#66BB6A", text: "#43A047" }, // Green
    { bg: "#FFF3E0", border: "#FFA726", text: "#FB8C00" }, // Orange
    { bg: "#F3E5F5", border: "#AB47BC", text: "#8E24AA" }, // Purple
    { bg: "#FFFDE7", border: "#FFEE58", text: "#FDD835" }, // Yellow
  ];

  const index = name.charCodeAt(0) % colorSets.length;
  return colorSets[index];
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const isSelectionEmpty = (selection: Record<string, NoteItem[]>) => {
  if (!selection || Object.keys(selection).length === 0) return true;
  return Object.values(selection).every(
    (notesArray: NoteItem[]) => Array.isArray(notesArray) && notesArray.length === 0,
  );
};

export const getPlaceOpeningHours = (
  openingHours: string[] | null | undefined,
) => {
  if (
    !openingHours ||
    !Array.isArray(openingHours) ||
    openingHours.length === 0
  ) {
    return { isOpen: false, statusText: "Unknown", detailText: null };
  }

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const now = new Date();
  const currentDayName = days[now.getDay()];

  const timeString = openingHours.find((h) => h.startsWith(currentDayName));

  if (!timeString)
    return { isOpen: false, statusText: "Closed", detailText: null };

  if (timeString.toLowerCase().includes("closed")) {
    return { isOpen: false, statusText: "Closed", detailText: null };
  }

  if (timeString.toLowerCase().includes("open 24 hours")) {
    return { isOpen: true, statusText: "Open", detailText: "24 hours" };
  }

  // Extract time part: "Monday: 12:00 – 4:00 PM, 7:00 – 11:00 PM" -> "12:00 – 4:00 PM, 7:00 – 11:00 PM"
  // Use substring to safely remove Day prefix regardless of spacing
  const cleanTime = timeString
    .substring(timeString.indexOf(":") + 1)
    .replace(/[ \u2009\u00A0\u202F]/g, " ") // Replace narrow/thin spaces/nbsp
    .trim();

  // Parse helper
  const parseTime = (t: string) => {
    // Matches 12:00PM, 12PM, 12:00 PM, 12 PM
    const match = t.match(/(\d+)(?::(\d+))?\s*(AM|PM)/i);
    if (!match) return null;
    const [h, m, p] = match.slice(1);
    let hours = parseInt(h);
    const minutes = m ? parseInt(m) : 0;

    if (p.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (p.toUpperCase() === "AM" && hours === 12) hours = 0;
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d;
  };

  // Handle multiple ranges (e.g., split shifts separated by comma)
  const ranges = cleanTime.split(",").map((r) => r.trim());

  let isOpen = false;
  let currentRangeEnd = "";
  let nextOpeningTime = "";

  for (const range of ranges) {
    // Split by endash, emdash, hyphen
    const parts = range.split(/[–—-]/).map((p) => p.trim());
    if (parts.length !== 2) continue;

    const start = parseTime(parts[0]);
    const end = parseTime(parts[1]);

    if (!start || !end) continue;

    // Handle overnight (e.g. 9 PM - 2 AM)
    if (end < start) {
      end.setDate(end.getDate() + 1);
    }

    if (now >= start && now <= end) {
      isOpen = true;
      currentRangeEnd = parts[1];
      break; // Found the current open range
    } else if (now < start) {
      // If we are before this range, and haven't found a closer "next opening", track this
      if (!nextOpeningTime) {
        nextOpeningTime = parts[0];
      }
    }
  }

  if (isOpen) {
    return {
      isOpen: true,
      statusText: "Open",
      detailText: `Closes at ${currentRangeEnd}`,
    };
  } else {
    // If closed, show next opening time today if available, else generic "Opens next day"
    return {
      isOpen: false,
      statusText: "Closed",
      detailText: nextOpeningTime
        ? `Opens at ${nextOpeningTime}`
        : "Opens next day", // Simplified
    };
  }
};

const getFirstPhotoFromSection = (
  media: MediaSection[],
  sectionName: string,
): string | undefined => {
  const sectionMedia = media.find(
    (m) => m?.placeSection?.section_name?.toLowerCase() === sectionName.toLowerCase(),
  );
  return (
    sectionMedia?.photos?.find((p) => p.is_correct)?.url ||
    sectionMedia?.photos?.[0]?.url
  );
};

export const getDisplayImage = (
  media: MediaSection[] | null | undefined,
  fallbackIcon: string = Icons.placeImg,
): string => {
  if (!media || !media.length) return fallbackIcon;

  // 1. Try entrance section first
  const entrancePhoto = getFirstPhotoFromSection(media, "entrance");
  if (entrancePhoto) return entrancePhoto;

  // 2. Try exit section
  const exitPhoto = getFirstPhotoFromSection(media, "exit");
  if (exitPhoto) return exitPhoto;

  // 3. Fallback: find any photo available in any section
  for (const section of media) {
    const photo =
      section.photos?.find((p) => p.is_correct)?.url ||
      section.photos?.[0]?.url;
    if (photo) return photo;
  }

  return fallbackIcon;
};

export const getMediaItems = (
  media: (MediaSection | ApiMedia)[] | null | undefined,
  filterCorrect: boolean = false,
): MediaItem[] => {
  if (!media) return [];

  return media.flatMap((m) => {
    const photos: MediaItem[] = (m.photos || [])
      .filter((p) => !filterCorrect || p.is_correct)
      .map((p) => ({ url: p.url, type: "image" as const }));

    let videoItems: MediaItem[] = [];
    if ('videos' in m && Array.isArray(m.videos) && m.videos.length > 0) {
      videoItems = m.videos
        .filter((v) => !filterCorrect || v.is_correct)
        .map((v) => ({ url: v.url, type: "video" as const }));
    } else if ('video' in m && m.video) {
      if (!filterCorrect || m.video.is_correct) {
        videoItems = [{ url: m.video.url, type: "video" as const }];
      }
    }

    return [...photos, ...videoItems];
  });
};

export const isIOS = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1000; // Distance in meters
};

export const validateAndCleanText = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9\s]/g, "");
};

export const isValidTextOnly = (value: string): boolean => {
  const regex = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s]*$/;
  return regex.test(value);
};

export const base64ToFile = (
  base64String: string,
  fileName: string,
  mimeType: string,
): File => {
  try {
    let base64Data = base64String;

    if (base64String.includes("base64,")) {
      base64Data = base64String.split("base64,")[1];
    }

    // Clean up the base64 string - replace URL-safe characters if needed
    base64Data = base64Data
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .replace(/\s/g, ""); // Remove any whitespace

    // Try to decode the base64 string
    try {
      // First try direct decoding
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new File([byteArray], fileName, { type: mimeType });
    } catch (e) {
      console.warn(
        "Direct base64 decoding failed, trying URI component decoding:",
        e,
      );

      // If direct decoding fails, try URI component decoding
      try {
        const decodedURI = decodeURIComponent(base64Data);
        const byteCharacters = atob(decodedURI);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new File([byteArray], fileName, { type: mimeType });
      } catch (uriError) {
        console.error("URI component decoding also failed:", uriError);
        throw new Error("Failed to decode base64 string");
      }
    }
  } catch (error) {
    console.error("Error converting base64 to file:", error);
    // Fallback: create an empty file with the expected name
    return new File([], fileName, { type: mimeType });
  }
};

export const detectBlur = async (
  imageDataUrl: string,
  blurThreshold: number = 150,
): Promise<BlurResult> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;

      // Set canvas size
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0);

      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Convert to grayscale and apply Laplacian filter for edge detection
      const width = canvas.width;
      const height = canvas.height;
      let variance = 0;
      const kernel = [
        [0, -1, 0],
        [-1, 4, -1],
        [0, -1, 0],
      ];

      // Apply Laplacian kernel
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          let sum = 0;

          for (let ky = 0; ky < 3; ky++) {
            for (let kx = 0; kx < 3; kx++) {
              const pixelY = y + ky - 1;
              const pixelX = x + kx - 1;
              const pixelIndex = (pixelY * width + pixelX) * 4;

              // Convert to grayscale
              const gray =
                (data[pixelIndex] +
                  data[pixelIndex + 1] +
                  data[pixelIndex + 2]) /
                3;
              sum += gray * kernel[ky][kx];
            }
          }

          variance += sum * sum;
        }
      }

      // Calculate average variance (blur score)
      const blurScore = variance / ((width - 2) * (height - 2));
      const isBlurry = blurScore < blurThreshold;

      resolve({ isBlurry, blurScore });
    };

    img.src = imageDataUrl;
  });
};
